import React, { useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { Copy, ShieldCheck } from "lucide-react";

export default function QrCheckInPage() {
  const [params] = useSearchParams();
  const { toast } = useToast();

  const code = useMemo(() => (params.get("code") || "").trim(), [params]);

  const canonical = code
    ? `https://recruitlook.com/qr/checkin?code=${encodeURIComponent(code)}`
    : "https://recruitlook.com/qr/checkin";

  const handleCopy = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      toast({ title: "Copied", description: "Check-in code copied to clipboard." });
    } catch {
      toast({
        title: "Couldn't copy",
        description: "Please press and hold to copy the code.",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Event Check-In QR | RecruitLook</title>
        <meta
          name="description"
          content="Open your RecruitLook event check-in QR code and show it to staff for fast entry."
        />
        <link rel="canonical" href={canonical} />
      </Helmet>

      <main className="min-h-[70vh] bg-background">
        <section className="container mx-auto max-w-2xl px-4 py-10">
          <header className="mb-6">
            <h1 className="font-heading text-3xl text-foreground">Event Check-In</h1>
            <p className="mt-2 text-muted-foreground">
              Show this screen to event staff, or tap copy and read the code aloud.
            </p>
          </header>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5" />
                Check-In Code
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {code ? (
                <>
                  <div className="rounded-lg bg-muted p-4">
                    <p className="text-xs text-muted-foreground">Code</p>
                    <p className="mt-1 break-all font-mono text-lg text-foreground">
                      {code}
                    </p>
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Button onClick={handleCopy} className="sm:flex-1">
                      <Copy className="mr-2 h-4 w-4" />
                      Copy Code
                    </Button>
                    <Button asChild variant="outline" className="sm:flex-1">
                      <Link to="/staff/check-in">Staff Check-In</Link>
                    </Button>
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Missing code. Please re-scan the QR from your Digital Wallet.
                </p>
              )}

              <Separator />

              <article className="space-y-2 text-sm text-muted-foreground">
                <p>
                  Tip: Some phone cameras will open a web page instead of showing the raw QR
                  text—that's normal. Staff can still scan it.
                </p>
              </article>
            </CardContent>
          </Card>
        </section>
      </main>
    </>
  );
}
